/**
 * Shared UI component for UI Stock Selector.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiStockProductOption, UiStockSelectorValue, UiStockVariantOption } from './ui-stock-selector.types';

@Component({
  selector: 'ui-stock-selector',
  standalone: true,
  imports: [UiSelectComponent, UiFormFieldComponent],
  templateUrl: './ui-stock-selector.component.html',
  styleUrl: './ui-stock-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiStockSelectorComponent {
  private readonly defaultCategories: UiSelectOption[] = [
    { label: 'SIM', value: 'SIM' },
    { label: 'Float', value: 'Float' },
    { label: 'Cash', value: 'Cash' },
    { label: 'Device', value: 'Device' }
  ];

  readonly value = input<UiStockSelectorValue>({ categoryId: '', productId: '', variantId: '', variantIds: [], quantity: null });
  readonly categories = input<UiSelectOption[]>([]);
  readonly products = input<UiStockProductOption[]>([]);
  readonly variants = input<UiStockVariantOption[]>([]);
  readonly categoryLabel = input('Category');
  readonly productLabel = input('Product');
  readonly variantLabel = input('Variant');
  readonly quantityLabel = input('Quantity');
  readonly quantityError = input('');
  readonly showVariant = input(true);
  readonly allowMultipleVariants = input(false);
  readonly showQuantity = input(true);
  readonly valueChange = output<UiStockSelectorValue>();

  readonly categoryOptions = computed<UiSelectOption[]>(() => {
    const configuredCategories = this.normalizeOptions(this.categories());

    if (configuredCategories.length > 0) {
      return configuredCategories;
    }

    const productCategories = this.normalizeOptions(
      this.products()
        .map((product) => product.categoryId)
        .filter(Boolean)
        .map((categoryId) => ({ label: categoryId, value: categoryId }))
    );

    return productCategories.length > 0 ? productCategories : this.defaultCategories;
  });

  readonly filteredProducts = computed(() => {
    const categoryId = this.value().categoryId;
    const products = this.products()
      .filter((product) => !categoryId || product.categoryId === categoryId)
      .map((product) => ({ label: product.label, value: product.value }));

    return this.normalizeOptions(products);
  });

  readonly filteredVariants = computed(() => {
    const productId = this.value().productId;
    const variants = this.variants()
      .filter((variant) => !productId || variant.productId === productId)
      .map((variant) => ({ label: variant.label, value: variant.value }));

    return this.normalizeOptions(variants);
  });

  selectCategory(categoryId: string): void {
    this.emit({ categoryId, productId: '', variantId: '', variantIds: [] });
  }

  selectProduct(productId: string): void {
    this.emit({ productId, variantId: '', variantIds: [] });
  }

  selectVariant(variantId: string): void {
    this.emit({ variantId, variantIds: variantId ? [variantId] : [] });
  }

  selectVariants(variantIds: string[]): void {
    this.emit({ variantId: variantIds[0] ?? '', variantIds });
  }

  setQuantity(value: string): void {
    const parsed = Number(value);
    this.emit({ quantity: Number.isFinite(parsed) ? parsed : null });
  }

  private emit(patch: Partial<UiStockSelectorValue>): void {
    this.valueChange.emit({ ...this.value(), ...patch });
  }

  private normalizeOptions(options: UiSelectOption[]): UiSelectOption[] {
    const seen = new Set<string>();

    return options
      .map((option) => ({
        ...option,
        label: String(option.label ?? '').trim(),
        value: String(option.value ?? '').trim()
      }))
      .filter((option) => {
        if (!option.label || !option.value || seen.has(option.value)) {
          return false;
        }

        seen.add(option.value);
        return true;
      });
  }
}
